import {ValidRecruiterCode} from 'Agent/Policies/models/RecruiterCode';
import {ExistingTaxIdentificationNumber} from 'Agent/Policies/models/TaxIdentificationNumber';
import {push} from 'connected-react-router';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult, fromResult, Loaded, Loading, LoadingAdditional} from 'Core/models/Async';
import {createValidatedEmail, ValidEmail} from 'Core/models/Email';
import {NonEmptyString} from 'Core/models/EmptyString';
import {Page} from 'Core/models/Page';
import {createValidatableEmptyPhone, createValidatedPhone, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {RoleId} from 'Core/models/Role';
import {createValidatedNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import {flatten, head} from 'fp-ts/lib/Array';
import * as E from 'fp-ts/lib/Either';
import {fold as foldEither, right, Right} from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {flatten as flattenOption, isSome, none, Option, some} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {nanoid} from 'nanoid';
import {all, call, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {Scope} from 'Shared/constants/Scope';
import axios from 'Shared/utilities/axiosInstance';
import {eq} from 'Shared/utilities/equals';
import {getAxiosConfig, getUrlFromUri, handleResponse} from 'Shared/utilities/fetchHelpers';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {prop} from 'Shared/utilities/prop';
import {getPageApiCallGenerator, PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import userApi from 'Users/api/userApi';
import {loadAgentMapping} from 'Users/ducks/agentAdministration';
import {
  changeUsers,
  deletePreregistration,
  deleteUser,
  fetchUsers,
  loadUser,
  resetUserPassword,
  UserAdministrationActionType,
  UserType,
} from 'Users/ducks/userAdministration';
import {loadExistingVip} from 'Users/ducks/vipUser';
import {AgencySupportVisibilityPermission, ValidAgencyAssociation} from 'Users/models/AgencyAssociation';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {AgentClaims} from 'Users/models/AgentClaims';
import {SavedAgentMapping, toPermission} from 'Users/models/AgentMapping';
import {ValidAgentNumber} from 'Users/models/AgentNumber';
import {UserDetailsModel, UserTypeModel, UserTypeModelKey} from 'Users/models/Claims';
import {SavedFieldSalesLeader} from 'Users/models/FieldSalesLeader';
import {SavedFieldVicePresident} from 'Users/models/FieldVicePresident';
import {PreregisteredUser} from 'Users/models/PreregisteredUser';
import {User} from 'Users/models/User';
import {SavedVip} from 'Users/models/Vip';
import {UserRouteConstants} from 'Users/Routes';
import {changePreregisteredUsers, getPreregisteredUsers} from './../ducks/userAdministration';
import {extractApplicationIdsFromScopeSaga} from './applicationIdSaga';
import {SailpointApplicationContainer} from '../../Shared/constants/UserRecovery';

export const getAgentClaims = (user: UserDetailsModel): Option<AgentClaims> => {
  return flattenOption(
    head(
      user.userTypes.map(
        UserTypeModel.match({
          [UserTypeModelKey.AgentSite]: agentClaims => some(agentClaims),
          default: () => none,
        })
      )
    )
  );
};

export const getTypedUser = (user: UserDetailsModel) => {
  const roleIds = flatten(
    user.userTypes.map(
      UserTypeModel.match({
        [UserTypeModelKey.AgentSite]: agentClaims => [
          ...(agentClaims.roles && agentClaims.roles.length > 0 ? agentClaims.roles.map(prop('id')) : []),
          ...agentClaims.agencyAssociations.map(association => association.roles.map(role => role.id)),
        ],
        default: () => [],
      })
    )
  );
  const agentClaims = getAgentClaims(user);

  if (roleIds.includes(RoleId.FieldSalesLeader) && isSome(agentClaims) && agentClaims.value.recruiterCode) {
    return User.FieldSalesLeader(
      new SavedFieldSalesLeader(
        NonEmptyString.create(user.firstName) as NonEmptyString,
        NonEmptyString.create(user.lastName) as NonEmptyString,
        createValidatedNonEmptyString(user.userName),
        user.mobilePhone ? createValidatedPhone(user.mobilePhone) : createValidatableEmptyPhone(),
        createValidatedEmail(user.email),
        ValidRecruiterCode.create(NonEmptyString.create(agentClaims.value.recruiterCode.value)) as Right<
          ValidRecruiterCode
        >,
        agentClaims.value.isNewYork,
        agentClaims.value.isAhl,
        user.id
      )
    );
  } else if (roleIds.includes(RoleId.FieldVicePresident) && isSome(agentClaims)) {
    return User.FieldVicePresident(
      new SavedFieldVicePresident(
        NonEmptyString.create(user.firstName) as NonEmptyString,
        NonEmptyString.create(user.lastName) as NonEmptyString,
        createValidatedNonEmptyString(user.userName),
        user.mobilePhone ? createValidatedPhone(user.mobilePhone) : createValidatableEmptyPhone(),
        createValidatedEmail(user.email),
        agentClaims.value.fieldSalesLeaders,
        user.id
      )
    );
  } else if (
    isSome(agentClaims) &&
    agentClaims.value.agencyAssociations &&
    agentClaims.value.agencyAssociations.length > 0
  ) {
    return User.AgentUser(
      new SavedAgentMapping(
        {
          agencyAssociations: agentClaims.value.agencyAssociations.map(association =>
            ValidAgencyAssociation.create(
              nanoid(),
              association.roles.map(role => role.id).includes(RoleId.AgencyAdministrator),
              new ExistingTaxIdentificationNumber(association.maskedIdentifier, association.encryptedIdentifier),
              association.agentNumbers.map(agentNumber =>
                ValidAgentNumber.create(agentNumber.value, agentNumber.isNewYork)
              ) as ValidAgentNumber[],
              association.roles
                .map(role => role.id)
                .map(toPermission)
                .filter(id => id !== '') as AgencySupportVisibilityPermission[]
            )
          ),
          email: new Loaded(right(ValidEmail.create(user.email) as ValidEmail)),
          firstName: NonEmptyString.create(user.firstName),
          lastName: NonEmptyString.create(user.lastName),
          phoneNumber: new Loaded(right(ValidPhoneNumber.create(user.mobilePhone) as ValidPhoneNumber)),
          userName: new Loaded(right(NonEmptyString.create(user.userName) as NonEmptyString)),
          isDisabled: false
        },
        user.id
      )
    );
  } else {
    return User.Vip(
      new SavedVip(
        NonEmptyString.create(user.firstName) as NonEmptyString,
        NonEmptyString.create(user.lastName) as NonEmptyString,
        new Loaded(right(NonEmptyString.create(user.userName) as NonEmptyString)) as Loaded<Right<NonEmptyString>>,
        new Loaded(right(ValidPhoneNumber.create(user.mobilePhone || '') as ValidPhoneNumber)) as Loaded<
          Right<ValidPhoneNumber>
        >,
        new Loaded(right(ValidEmail.create(user.email) as ValidEmail)) as Loaded<Right<ValidEmail>>,
        isSome(agentClaims) && agentClaims.value.isAhl,
        isSome(agentClaims) && agentClaims.value.isNewYork,
        isSome(agentClaims) && agentClaims.value.roles.some(eq('id', RoleId.AllstateExternalAdministrator)),
        user.id
      )
    );
  }
};

export function* loadUserSaga() {
  function* onLoadUser(action: ReturnType<typeof loadUser>) {
    const userEither: AsyncResult<UserDetailsModel> = yield call(userApi.getUser, action.payload.userId);
    const doNothing = function*(): IterableIterator<any> {};

    function* routeToVipScreen(vip: SavedVip) {
      yield put(yield call(loadExistingVip, new Loaded(vip)));
      yield put(push(UserRouteConstants.EDIT_VIP_USER(vip.id)));
    }

    function* routeToAgentScreen(agent: SavedAgentMapping) {
      yield put(yield call(loadAgentMapping, new Loaded(agent)));
      yield put(push(UserRouteConstants.EDIT_AGENT(agent.id)));
    }

    function* routeToUserScreen(user: UserDetailsModel) {
      const typedUser = getTypedUser(user);
      yield User.match(typedUser, {
        AgentUser: routeToAgentScreen,
        Vip: routeToVipScreen,
        default: doNothing,
      });
    }
    yield pipe(
      userEither,
      foldEither(doNothing, user => routeToUserScreen(user))
    );
  }

  yield takeLatest(UserAdministrationActionType.LOAD_USER, onLoadUser);
}

export const getUsers = (action: ReturnType<typeof fetchUsers>) =>
  PageFetchAction.match({
    Additional: additionalPage => mergeWithNextPage<UserType>(additionalPage),
    Initial: () => userApi.getUsers(action.payload.filter),
  })(action.payload);

export function* getUsersSaga() {
  yield takeLatest(UserAdministrationActionType.FETCH_USERS, getPageApiCallGenerator(getUsers, changeUsers));
}

export function* deleteUserSaga() {
  yield takeLatest(UserAdministrationActionType.DELETE_USER, function*(action: ReturnType<typeof deleteUser>) {
    const {userId, onComplete} = action.payload;
    const applicationIds = (yield extractApplicationIdsFromScopeSaga(Scope.DeleteUser)) as number[];

    const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
      (state: ReducerState) => state.user.profile.selectedAgency
    );
    const encryptedIdentifier = O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined;

    const result: AsyncResult<unknown> = yield userApi.deleteUser(userId, applicationIds, encryptedIdentifier);

    if (E.isRight(result)) {
      yield put(createSnackbar('User Deleted Successfully'));
      onComplete();
    } else {
      yield put(createSnackbar('Unable To Delete User'));
      onComplete();
    }
  });
}

export function* resetUserPasswordSaga() {
  yield takeLatest(UserAdministrationActionType.RESET_USER_PASSWORD, function*(
    action: ReturnType<typeof resetUserPassword>
  ) {
    const {userId, onComplete} = action.payload;

    const getValidatedAddress = (userId: string) => {
      const axiosConfig = getAxiosConfig();
      const ADDRESS_ROOT_URL: string = `api/user/v1/portal/${userId}/password/resetPassword/${SailpointApplicationContainer.BR}`;
      return axios
        .post(getUrlFromUri(`${ADDRESS_ROOT_URL}`), none, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
          },
        })
        .then(handleResponse);
    };

    const result: AsyncResult<unknown> = yield getValidatedAddress(userId);

    if (E.isRight(result)) {
      yield put(createSnackbar('Password Reset Successful!'));
      onComplete();
    } else {
      yield put(createSnackbar('Unable To Reset User Password'));
      onComplete();
    }
  });
}

function* loadPreregisteredUersSaga() {
  function* getPreregisteredUsersApi(action: ReturnType<typeof getPreregisteredUsers>) {
    const {filter, page, encryptedIdentifier} = action.payload;

    if (action.payload.page) {
      yield put(changePreregisteredUsers(new LoadingAdditional(action.payload.page)));
    } else {
      yield put(changePreregisteredUsers(new Loading(action.payload.page)));
    }

    const applicationIds = (yield extractApplicationIdsFromScopeSaga(Scope.SearchPreregistrationUsers)) as number[];
    const result: AsyncResult<Page<PreregisteredUser>> = yield page
      ? call(mergeWithNextPage, {existingPage: page})
      : call(userApi.getPreregisteredUsers, filter, applicationIds, encryptedIdentifier);

    yield pipe(
      result,
      E.map(fromResult),
      E.fold(
        value => put(changePreregisteredUsers(value)),
        value => put(changePreregisteredUsers(value))
      )
    );
  }

  yield takeLatest(UserAdministrationActionType.GET_PREREGISTERED_USERS, getPreregisteredUsersApi);
}

function* deletePreregistrationSaga() {
  function* deletePreregistrationApiSaga(action: ReturnType<typeof deletePreregistration>) {
    const {encryptedIdentifier, userId, filter} = action.payload;
    const applicationIds = (yield extractApplicationIdsFromScopeSaga(Scope.DeletePreregistrationUser)) as number[];

    yield call(userApi.deletePreregistration, userId, applicationIds, encryptedIdentifier);

    yield put(getPreregisteredUsers(filter, undefined, encryptedIdentifier));
  }

  yield takeEvery(UserAdministrationActionType.DELETE_PREREGISTRATION, deletePreregistrationApiSaga);
}

export default function*() {
  yield all([
    deleteUserSaga(),
    resetUserPasswordSaga(),
    getUsersSaga(),
    loadUserSaga(),
    loadPreregisteredUersSaga(),
    deletePreregistrationSaga(),
  ]);
}
